import {
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  FormControl,
  IconButton,
  Stack,
  useMediaQuery
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { Close } from "@material-ui/icons";
import { LoadingButton } from "@material-ui/lab";
import { useTranslation } from "react-i18next";
import type { PropsWithChildren } from "react";

type CloseListingDealDialogProps = PropsWithChildren<{
  open: boolean;
  loading: boolean;
  onClose: () => void;
  handleSubmit: () => void;
}>;

export function CloseListingDealDialog({
  open,
  loading,
  children,
  onClose,
  handleSubmit
}: CloseListingDealDialogProps): JSX.Element {
  const { t: translate } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Dialog open={open} fullWidth fullScreen={isMobile} maxWidth="md" onClose={onClose}>
      {/* `overflow: hidden` is what makes DialogContent the scroller. Without it the form
          grows to its content and the Paper scrolls instead — and the theme's
          `overscrollBehavior: contain` on every DialogContent then swallows the wheel and
          the touch drag, so only the scrollbar moved it and a phone could not scroll at all.
          `height: 100%` fills a full-screen Paper, so the buttons stay at the bottom of a
          phone's screen; on a centred Paper (auto height) it is auto and changes nothing. */}
      <FormControl sx={{ overflow: "hidden", height: "100%" }} component="form" onSubmit={handleSubmit}>
        {/* The heading is rendered by the content itself (it is shared with the edit
            dialog), so this row carries only the close button. Disabled while saving,
            like Cancel below — `onClose` refuses anyway. */}
        <Stack direction="row" justifyContent="flex-end" sx={{ px: 1.5, pt: 1.5 }}>
          <IconButton disabled={loading} aria-label={translate("general.close")} onClick={onClose}>
            <Close />
          </IconButton>
        </Stack>
        <DialogContent sx={{ pt: 0 }}>{children}</DialogContent>
        <DialogActions sx={{ justifyContent: "center" }}>
          <Button variant="contained" color="error" disabled={loading} onClick={onClose}>
            {translate("general.cancel")}
          </Button>
          <LoadingButton type="submit" color="info" variant="contained" loading={loading}>
            {translate("general.confirm")}
          </LoadingButton>
        </DialogActions>
      </FormControl>
    </Dialog>
  );
}
